import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderAny from "../components/header";
import MobileNav from "../components/header-mobile";
import Footer from "../components/footer";

const FAQ_CATEGORIES = [
    {
        id: "general",
        label: "General",
        faqs: [
            {
                q: "How do I create a digital construction portfolio?",
                a: "With ConRa, you can build a verified construction portfolio in minutes. Upload project photos, site records, and work history directly from your mobile device to showcase your true track record to clients.",
            },
            {
                q: "How do clients verify contractor work history?",
                a: "Instead of relying on paper verification forms or unverified PDF resumes, ConRa provides instant cryptographic proof of completed construction builds and contractor credentials.",
            },
            {
                q: "Can I replace manual contractor verification forms with ConRa?",
                a: "Yes. ConRa eliminates slow, manual experience verification letters by maintaining a tamper-proof digital record of project milestones.",
            },
            {
                q: "What is a contractor verification checklist?",
                a: "A contractor verification checklist is a set of proof points—like licensing, business registration, and past project history—used to vet a builder. ConRa automates this entire checklist digitally in one verified profile.",
            },
        ],
    },
    {
        id: "contractors",
        label: "Contractors & Developers",
        faqs: [
            {
                q: "What if the site has no internet connection at the moment of capture?",
                a: "ConRa captures and signs media offline. The GPS data, timestamp and C2PA signature are embedded at the moment of capture — the upload happens when connectivity returns, but the evidence is already sealed.",
            },
            {
                q: "Can a competitor or the public see my portfolio?",
                a: "Access is controlled entirely by the project code. Nothing on ConRa is publicly searchable. You decide who a code is shared with — and you can revoke that access when a project ends.",
            },
            {
                q: "Is my historical project data preserved if I switch devices or team members?",
                a: "Yes. Your ConRa project history is tied to your project code and company account — not the device. Switch devices, onboard new team members or hand over to a new site manager without losing a single milestone.",
            },
            {
                q: "Do I need to install anything?",
                a: "Install the free ConRa app to capture on-site photos with verified metadata. Anyone viewing your portfolio through a shared project code needs nothing more than a web browser.",
            },
            {
                q: "Does ConRa replace my website?",
                a: "No — ConRa complements your website. Your website is a marketing brochure. ConRa is independently verifiable evidence. Together, they cover different kinds of trust.",
            },
            {
                q: "How much does it cost to get started?",
                a: "Start with a single project for free. Publish verified milestones, generate your project code and share your portfolio before committing to any paid plan.",
            },
        ],
    },
    {
        id: "banks",
        label: "Banks & Lenders",
        faqs: [
            {
                q: "Is ConRa's verification legally defensible?",
                a: "Every capture is secured with the C2PA content authenticity standard — the same open standard used across the media and imaging industry — creating a tamper-evident record you can stand behind in audits, disputes, or board reviews.",
            },
            {
                q: "How does this fit into our existing disbursement workflow?",
                a: "ConRa slots into your existing milestone-release process. You define the milestone criteria in ConRa, the developer fulfils them on site, and your team reviews the verified evidence before releasing funds. No new infrastructure required on your side.",
            },
            {
                q: "What happens if a contractor submits an old or edited photo?",
                a: "ConRa's verification layer automatically flags any photo that fails GPS, timestamp or content authenticity checks. Your team is notified before funds are released, and the audit trail captures the failed submission for your records.",
            },
        ],
    },
];

const FaqPage = () => {
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState("general");
    const [openIndex, setOpenIndex] = useState(null);

    const currentCategory = FAQ_CATEGORIES.find((c) => c.id === activeCategory);

    const handleCategoryChange = (id) => {
        setActiveCategory(id);
        setOpenIndex(null);
    };

    return (
        <div>
            <div className="web-only"><HeaderAny /></div>
            <div className="mobile-only"><MobileNav /></div>

            <section id="faq" style={{ paddingTop: "80px", paddingBottom: "60px" }}>
                <div className="faq-container">
                    <h1 className="h1-48-800">Your Questions, <span>Answered</span></h1>
                    <p className="faq-description">
                        Everything you need to know about ConRa — from getting started to advanced verification.
                    </p>

                    {/* Category tabs */}
                    <div className="faq-tabs" style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap", marginBottom: "32px" }}>
                        {FAQ_CATEGORIES.map(({ id, label }) => (
                            <button
                                key={id}
                                onClick={() => handleCategoryChange(id)}
                                style={{
                                    padding: "8px 20px",
                                    borderRadius: "20px",
                                    border: "1px solid",
                                    borderColor: activeCategory === id ? "var(--color-primary)" : "rgba(24,24,24,0.2)",
                                    background: activeCategory === id ? "var(--color-primary)" : "white",
                                    color: activeCategory === id ? "white" : "var(--color-text-primary)",
                                    fontWeight: activeCategory === id ? 600 : 400,
                                    fontSize: "1.4rem",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* FAQ accordion */}
                    <div className="faq-list">
                        {currentCategory.faqs.map(({ q, a }, index) => (
                            <div
                                key={index}
                                className={`faq-item ${openIndex === index ? "open" : ""}`}
                                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                            >
                                <div className="faq-question">
                                    {q}
                                    <span className="icon">{openIndex === index ? "−" : "+"}</span>
                                </div>
                                {openIndex === index && <div className="faq-answer">{a}</div>}
                            </div>
                        ))}
                    </div>

                    <div className="faq-footer">
                        <p>Still have questions? We're here to help!</p>
                        <button className="faq-button" onClick={() => navigate("/contact-us")}>Contact Support</button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default FaqPage;
